import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Divider,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { BankIcon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Checkbox,
  ConfirmDialog,
  DataRow,
  Drawer,
  Select,
  TextField,
} from "@outpave/ds";
import PlaidBankLink from "./PlaidBankLink";
import { poolLabel } from "./poolLabel";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { RenderAccountStatus } from "components/common/RenderAccountStatus";
import CustomSkeleton from "components/common/CustomSkeleton";
import type {
  CompletePlatformFundingSourceInput,
  PlatformFundingBankDetails,
  PlatformFundingSource,
  PlatformRewardsPool,
} from "./types";

interface ManageBankDrawerProps {
  open: boolean;
  onClose: () => void;
  pools: PlatformRewardsPool[];
  fundingSources: PlatformFundingSource[];
  loading: boolean;
  error: boolean;
  saving: boolean;
  onAdd: (input: {
    destinationAccountId: string;
    name: string;
  }) => Promise<PlatformFundingSource | undefined>;
  onConfirmBank: (
    clientSecret: string,
    details: PlatformFundingBankDetails,
    publishableKey?: string | null,
  ) => Promise<string>;
  onComplete: (
    id: string,
    input: CompletePlatformFundingSourceInput,
  ) => Promise<PlatformFundingSource | undefined>;
  onRemove: (id: string) => void;
}

const EMPTY_DETAILS: PlatformFundingBankDetails = {
  accountHolderName: "",
  email: "",
  routingNumber: "",
  accountNumber: "",
  accountType: "checking",
};

const ManageBankDrawer: React.FC<ManageBankDrawerProps> = ({
  open,
  onClose,
  pools,
  fundingSources,
  loading,
  error,
  saving,
  onAdd,
  onConfirmBank,
  onComplete,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [destinationAccountId, setDestinationAccountId] = useState("");
  const [pending, setPending] = useState<PlatformFundingSource | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [details, setDetails] =
    useState<PlatformFundingBankDetails>(EMPTY_DETAILS);
  const [confirming, setConfirming] = useState(false);
  const [microDepositUrl, setMicroDepositUrl] = useState<string | null>(null);
  const [achAuthorizationAccepted, setAchAuthorizationAccepted] =
    useState(false);
  const [plaidOpen, setPlaidOpen] = useState(false);
  const destination = destinationAccountId || pools[0]?.platformAccountId || "";
  const linksThroughPlaid =
    pools.find((pool) => pool.platformAccountId === destination)?.issuer ===
    "HIGHNOTE";
  const detailsComplete =
    details.accountHolderName.trim() !== "" &&
    details.email.trim() !== "" &&
    details.routingNumber.trim() !== "" &&
    details.accountNumber.trim() !== "";

  const [removing, setRemoving] = useState<PlatformFundingSource | null>(null);

  const describeBank = (row: PlatformFundingSource) => {
    const pool =
      pools.length > 1
        ? pools.find(
            (each) => each.platformAccountId === row.destinationAccountId,
          )
        : undefined;
    return [
      row.last4 && `${row.institutionName ?? ""} •••• ${row.last4}`.trim(),
      pool && poolLabel(pool),
    ]
      .filter(Boolean)
      .join(", ");
  };

  const handleAdd = async () => {
    if (!name.trim() || !destination) return;
    const begun = await onAdd({
      destinationAccountId: destination,
      name: name.trim(),
    });
    if (!begun) return;
    if (begun.plaidLinkToken) {
      setPending(begun);
      setMicroDepositUrl(null);
      setPlaidOpen(true);
      return;
    }
    if (begun.setupIntentClientSecret) {
      setPending(begun);
      setMicroDepositUrl(null);
    }
    setName("");
  };

  const finishBank = async (input: CompletePlatformFundingSourceInput) => {
    if (!pending) return;
    setConfirming(true);
    try {
      const completed = await onComplete(pending.id, input);
      if (!completed) return;
      setMicroDepositUrl(completed.microDepositUrl ?? null);
      setPending(null);
      setSetupIntentId(null);
      setName("");
      setDetails(EMPTY_DETAILS);
      setAchAuthorizationAccepted(false);
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirm = async () => {
    if (!pending?.setupIntentClientSecret || !detailsComplete) return;
    setConfirming(true);
    try {
      const confirmed =
        setupIntentId ??
        (await onConfirmBank(
          pending.setupIntentClientSecret,
          details,
          pending.publishableKey,
        ));
      setSetupIntentId(confirmed);
      await finishBank({ setupIntentId: confirmed });
    } finally {
      setConfirming(false);
    }
  };

  const handleLinked = async (
    plaidPublicToken: string,
    plaidAccountIds: string[],
  ) => {
    setPlaidOpen(false);
    await finishBank({
      plaidPublicToken,
      ...(plaidAccountIds.length > 0 && { plaidAccountIds }),
      achAuthorizationAccepted: true,
    });
  };

  const handleCancelBank = () => {
    setPending(null);
    setSetupIntentId(null);
    setPlaidOpen(false);
    setDetails(EMPTY_DETAILS);
    setAchAuthorizationAccepted(false);
  };

  const setDetail =
    (field: keyof PlatformFundingBankDetails) =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setDetails((current) => ({ ...current, [field]: event.target.value }));

  const busy = saving || confirming;

  return (
    <Drawer
      open={open}
      disableEnforceFocus={plaidOpen}
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      title={t("Funding sources")}
      loading={busy}
      footer={
        <Button variant="primary-outlined" onClick={onClose} disabled={busy}>
          {t("Close")}
        </Button>
      }
    >
      {error ? (
        <Alert severity="error">{t("FundingSourcesLoadError")}</Alert>
      ) : (
        <Stack gap="var(--space-6)">
          <Stack gap="var(--space-3)">
            <Typography variant="h5">{t("Funding banks")}</Typography>
            {loading && fundingSources.length === 0 ? (
              <CustomSkeleton variant="list" rows={2} hideCircular />
            ) : fundingSources.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("No funding sources yet")}
              </Typography>
            ) : (
              <Stack gap="var(--space-2)">
                {fundingSources.map((row) => (
                  <DataRow
                    key={row.id}
                    type="amount"
                    icon={{
                      glyph: <HugeiconsIcon icon={BankIcon} size={18} />,
                      tone: "brand",
                    }}
                    title={row.name ?? row.institutionName ?? row.id}
                    subtitle={describeBank(row) || undefined}
                    tag={
                      row.status === "ACTIVE" ? undefined : (
                        <RenderAccountStatus
                          accountStatus={row.status}
                          isDeactivateRequested={false}
                        />
                      )
                    }
                    trailing={
                      <Button
                        size="small"
                        variant="danger-outlined"
                        disabled={busy}
                        onClick={() => setRemoving(row)}
                      >
                        {t("Remove")}
                      </Button>
                    }
                  />
                ))}
              </Stack>
            )}
          </Stack>
          {microDepositUrl && (
            <Alert severity="info">
              {t("FundingBankVerifying")}{" "}
              <Link href={microDepositUrl} target="_blank" rel="noopener">
                {t("Verify microdeposits")}
              </Link>
            </Alert>
          )}
          <Divider />
          {pending?.setupIntentClientSecret ? (
            <Stack gap="var(--space-4)">
              <Typography variant="h5">{t("Bank details")}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t("FundingBankDetailsCaption", { name: pending.name ?? "" })}
              </Typography>
              <TextField
                label={t("Account holder name")}
                fullWidth
                value={details.accountHolderName}
                onChange={setDetail("accountHolderName")}
              />
              <TextField
                label={t("Email")}
                fullWidth
                value={details.email}
                onChange={setDetail("email")}
              />
              <TextField
                label={t("Routing number")}
                fullWidth
                value={details.routingNumber}
                onChange={setDetail("routingNumber")}
              />
              <TextField
                label={t("Account number")}
                fullWidth
                value={details.accountNumber}
                onChange={setDetail("accountNumber")}
              />
              <Select
                label={t("Account type")}
                fullWidth
                options={[
                  { id: "checking", label: t("Checking") },
                  { id: "savings", label: t("Savings") },
                ]}
                value={details.accountType}
                onChange={(id) =>
                  setDetails((current) => ({
                    ...current,
                    accountType: id === "savings" ? "savings" : "checking",
                  }))
                }
              />
              <Inline gap="var(--space-3)">
                <Button
                  variant="primary-filled"
                  onClick={handleConfirm}
                  disabled={busy || !detailsComplete}
                >
                  {t("Confirm bank")}
                </Button>
                <Button
                  variant="default-outlined"
                  onClick={handleCancelBank}
                  disabled={busy}
                >
                  {t("Cancel")}
                </Button>
              </Inline>
            </Stack>
          ) : pools.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("NoOwnedPool")}
            </Typography>
          ) : (
            <Stack gap="var(--space-4)">
              <Typography variant="h5">{t("Add funding bank")}</Typography>
              <TextField
                label={t("Bank nickname")}
                required
                fullWidth
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
              {pools.length > 1 && (
                <Select
                  label={t("Pool account")}
                  fullWidth
                  options={pools.map((pool) => ({
                    id: pool.platformAccountId,
                    label: poolLabel(pool),
                  }))}
                  value={destination || null}
                  onChange={(id) => setDestinationAccountId(id ?? "")}
                  clearable={false}
                />
              )}
              {linksThroughPlaid && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={achAuthorizationAccepted}
                      onChange={(event) =>
                        setAchAuthorizationAccepted(event.target.checked)
                      }
                    />
                  }
                  label={t("FundingAchAuthorization")}
                />
              )}
              <Button
                variant="primary-filled"
                onClick={handleAdd}
                disabled={
                  busy ||
                  plaidOpen ||
                  !name.trim() ||
                  !destination ||
                  (linksThroughPlaid && !achAuthorizationAccepted)
                }
              >
                {t("Add funding bank")}
              </Button>
              {plaidOpen && pending?.plaidLinkToken && (
                <PlaidBankLink
                  token={pending.plaidLinkToken}
                  onLinked={handleLinked}
                  onExit={handleCancelBank}
                />
              )}
            </Stack>
          )}
        </Stack>
      )}
      <ConfirmDialog
        open={Boolean(removing)}
        title={t("Remove Account")}
        message={t("RemoveFundingBankMessage", {
          name: removing?.name ?? removing?.institutionName ?? "",
        })}
        confirmLabel={t("Remove")}
        cancelLabel={t("Cancel")}
        tone="danger"
        onConfirm={() => {
          if (removing) onRemove(removing.id);
          setRemoving(null);
        }}
        onCancel={() => setRemoving(null)}
      />
    </Drawer>
  );
};

export default ManageBankDrawer;
